import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Pencil,
  Trash2,
  UserPlus,
  CircleDollarSign,
  CircleCheck,
  Ellipsis,
  RotateCcw,
} from "lucide-react";
import {
  AcDialog,
  Button,
  Checkbox,
  Dropzone,
  EmptyState,
  MenuButton,
  Modal,
  Row,
  Rows,
  Spinner,
  Icon,
  type ActionMenuItem,
} from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useBandContext, useCapability } from "../../app/bandContext.js";
import {
  useShow,
  useDeleteShow,
  useShowMembers,
  useUpdateShow,
  useRuns,
  useSetShowAnnounced,
} from "../../app/hooks/useShows.js";
import { orgInitials } from "../../app/hooks/useOrg.js";
import { useAccount } from "../../app/hooks/useAccount.js";
import { useBandTeam } from "../../app/hooks/useBandTeam.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { ApiError, errorMessage } from "../../app/hooks/apiError.js";
import { SettlementPanel } from "./SettlementPanel.js";
import { ReopenShowButton } from "./ReopenShowModal.js";
import { DayOfShowSection } from "./DayOfShowSection.js";
import { InviteMembersModal } from "./InviteMembersModal.js";
import { BandBackOut, MemberBackOut } from "./ShowBackOut.js";
import { useActPhotoSource, ActAvatar, type ActPhotoSource } from "./actPhotos.js";
import { AcMark } from "./AcMark.js";
import { SpotifyArtistLink } from "../shared/VendorAttribution.js";
import { useShowShares, type SharedShowDetail } from "../../app/hooks/useShowShares.js";
import {
  useMediaObjectUrl,
  useSharedFlyerUrl,
  useUploadMedia,
  mediaKindForFile,
} from "../../app/hooks/useMedia.js";
import { showBodyFrom } from "./lib/showBody.js";
import {
  useSettlement,
  useUpdateSettlement,
  useSettleBox,
  useCloseOut,
} from "../../app/hooks/useMoney.js";
import { longDate, tMinus, todayIso, formatTime12, shortPersonName } from "./lib/bookingRows.js";
import { markDepositReceivedBody } from "./lib/settlementBody.js";
import { slotRoleLabel } from "./sharedShowFormat.js";
import styles from "./ShowPreview.module.scss";

type ShowDetail = components["schemas"]["ShowDetail"];
type LineupSlot = components["schemas"]["LineupSlotDto"];
type ShareDto = components["schemas"]["ShareDto"];

export interface ShowPreviewProps {
  showId: string;
  onEdit: (showId: string) => void;
  onOpenGuests: (showId: string) => void;
  /** Close the modal — back to the still-mounted Booking list underneath. */
  onBack: () => void;
  /**
   * GUEST MODE (ART-165). Render THIS view-show modal read-only from a guest's shared-show matrix instead of
   * the owner-fenced `useShow` — the guest reuses the owner's view modal, not a bespoke one. The money-gated
   * settlement, the members roster and the owner actions (publish / delete / mark-deposit) are dropped; the
   * flyer serves from the guest-reachable route; the status shown is the HOST show's status; and "Edit show"
   * (gated on `schedule:write`, like the owner's) opens the guest editor. `showId` is the host show id.
   */
  guest?: SharedShowDetail;
}

/** The subset of a show the preview BODY renders — so a guest's money-free matrix and an owner's full record
 *  drive one body. Everything money/members/publish/delete is owner-gated below, never read off this. */
interface PreviewView {
  venueName?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  date?: string | null;
  status?: string | null;
  doorsTime?: string | null;
  capacity?: number | null;
  promoter?: string | null;
  ticketUrl?: string | null;
  announcedOn?: string | null;
  flyerMediaId?: string | null;
  contractMediaId?: string | null;
  bandId?: string | null;
  lineup?: LineupSlot[] | null;
}

/**
 * The show details MODAL — the read-only show record plus the money-gated settlement workbench, drawn over a
 * still-mounted Booking list.
 *
 * It was a full-screen route until 2026-08-23 (TASK-057). The module spec is explicit that View "open[s] a
 * fixed-position popup on top of the Booking page — no URL change, no navigation", and the audit's ruling 10
 * had logged the divergence as awaiting exactly this call. The URL is unchanged, so the six backend push
 * routes that deep-link `/booking/shows/{showId}` still resolve here; as a routed child of `/booking` a cold
 * start now paints the list behind the scrim instead of a bare workbench.
 *
 * LAYOUT is the legacy `ShowDetailsModal` three-column body: a left rail of show facts, a center column of
 * door/VIP/splits inputs, and a right rail of payouts / expenses / merch — over a footer action bar. The
 * center and right columns are the SETTLEMENT, so they render inside `SettlementPanel`, which self-gates on
 * money:full/per_show and returns null for a no-money seat (the A3 rule). That is why this file composes a
 * layout rather than owning money state: the money boundary stays exactly where it already was, and a
 * money-free seat gets the left rail and the roster with no empty columns beside them.
 */
export function ShowPreview({
  showId,
  onEdit,
  onOpenGuests,
  onBack,
  guest,
}: ShowPreviewProps) {
  // Guest mode reads the shared-show matrix (passed in), never the owner-fenced `useShow` (disabled here).
  const show = useShow(guest ? undefined : showId);
  const runs = useRuns();
  // The acting band's own identity (name), for the headliner fallback below.
  const account = useAccount();
  const canWrite = useCapability("schedule:write");
  const canGuests = useCapability("guests:read");
  // Reopen reverts a settled show (worksheet + lifecycle dot) — money:full only, matching the settlement route.
  const canReopen = useCapability("money:full");
  // Close-out (Outstanding Payments) is money:full OR money:per_show — the same gate that route takes. Both read
  // unconditionally: `||` short-circuits, so folding them into one expression makes the second hook conditional
  // and breaks hook ORDER (the `useMarkDepositReceived` rule below).
  const canPerShow = useCapability("money:per_show");
  const canCloseMoney = canReopen || canPerShow;
  const deleteShow = useDeleteShow();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [invitingMembers, setInvitingMembers] = useState(false);
  // GUEST "Back out" (ART-229 + the member follow-up): the error slot for the BAND-decline path, which
  // GuestBackOut surfaces here (its member-Pass path toasts on its own). See GuestBackOut for the two kinds.
  const [backOutError, setBackOutError] = useState<string | null>(null);
  /** The DOS run sheet, opened from the footer as its own modal (the reference's shape). */
  const [dosOpen, setDosOpen] = useState(false);

  // Share state rides the LINEUP chips (parity, 2026-08-23) rather than a card of its own. `ShareDto.slotId`
  // joins a share to the slot it was sent for, so a chip can say whether that act is attached or still invited.
  // Sharing is no longer a manual action here (ART-165): a friend band on the lineup is auto-invited when the
  // lineup is saved (ShowsService.ReplaceLineupAsync), so this read is now STATUS-ONLY — the "· invited" /
  // "attached" badge — and there is no "Share with X" button to launch.
  const shares = useShowShares(showId);
  const shareBySlot = new Map(
    (shares.data?.shares ?? [])
      .filter((s) => s.slotId)
      .map((s) => [s.slotId!, s] as const),
  );

  const owner = show.data?.data;

  // The normalized record the BODY renders from — a guest reads the money-free matrix (the HOST show's status,
  // basics, lineup, flyer; everything private is structurally absent), an owner the full record. ONE body
  // renders both; only the money / members / publish / delete parts below are owner-gated. The guest lineup is
  // mapped up to the slot shape the chip + act-photo resolver take (the two vendor fields it lacks are null).
  const d: PreviewView | undefined = guest
    ? {
        venueName: guest.venueName, city: guest.city, state: guest.state, country: guest.country,
        date: guest.date, status: guest.showStatus, doorsTime: guest.doorsTime, capacity: guest.capacity,
        promoter: null, ticketUrl: guest.ticketUrl, announcedOn: null,
        flyerMediaId: guest.flyerMediaId, contractMediaId: null, bandId: guest.ownerBandId,
        lineup: (guest.lineup ?? []).map((s) => ({
          slotId: s.slotId, position: s.position, slotRole: s.slotRole, artistName: s.artistName,
          friendBandId: s.friendBandId, spotifyArtistId: null, spotifyImageUrl: null,
          friendBandAvatarMediaId: s.friendBandAvatarMediaId,
        })),
      }
    : owner;

  // The band whose show this is. The record's own `bandId` is the authority (the guest's is the HOST band);
  // the mounted context is the fallback for the tick before an owner read lands.
  const { bandId } = useBandContext();
  const ownerBandId = d?.bandId ?? bandId;
  // Every act's photo, from the ONE resolver the editor's lineup + run-of-show sections use. The band avatar
  // route is tenant-less, so it resolves a friend/host act's photo for a guest too. Called here rather than
  // inside the chip because hooks may not run inside a `.map`, and unconditionally above the loading/error
  // returns because hook ORDER is not allowed to depend on the fetch.
  const actSource = useActPhotoSource();
  // A guest's flyer bytes live in the host tenant, so they serve from the guest-reachable shared route.
  const guestFlyer = useSharedFlyerUrl(guest?.showId ?? null, guest?.flyerMediaId != null, "sm");

  // Re-close a REOPENED show straight from this preview (Ryan, ART-241): once a show is reopened here, offer
  // "Close out show" in the footer in place of "Add to Bandsintown", so a re-close needs no trip to Outstanding
  // Payments. Two things make this safe rather than a button that 409s:
  //  • SCOPE — a show in a run/tour has no bare "show" close-out scope (it settles at its run/tour), so we close
  //    the scope reopen actually cleared, resolved exactly as the server does (run → tour → show).
  //  • CLOSABLE — offered only when the scope's every item is settled (`canCloseOut`), which a reopened show
  //    always is (reopen unlocks the worksheet but never un-marks a paid item). A fresh confirmed show has no
  //    worksheet, so `canCloseOut` is false and "Add to Bandsintown" is unchanged.
  // A reopened show and an in-progress-but-never-closed show are indistinguishable in the data, so this keys on
  // "confirmed AND closable" — which is precisely the state a reopen leaves, and the one where "close it out" is
  // the true next action.
  const closeScope: { kind: string; id: string } = owner?.runId
    ? { kind: "run", id: owner.runId }
    : owner?.tourId
      ? { kind: "tour", id: owner.tourId }
      : { kind: "show", id: showId };
  const settleBox = useSettleBox(
    closeScope.kind,
    closeScope.id,
    !guest && canCloseMoney && owner?.status === "confirmed",
  );
  const canCloseOutShow = !guest && owner?.status === "confirmed" && settleBox.data?.canCloseOut === true;

  // The overflow's contents, each still gated by the capability it needs — the gate did not move when the
  // button did. Order is the bar's old left-to-right order, so muscle memory survives the change.
  const deposit = useMarkDepositReceived(showId);
  const moreActions: ActionMenuItem[] = [
    // `Day of show` opens the run sheet as its own modal (the reference's footer behaviour).
    ...(canGuests
      ? [
          {
            label: "Day of show",
            onSelect: () => setDosOpen(true),
          },
          {
            label: "Guest list",
            onSelect: () => onOpenGuests(showId),
          },
        ]
      : []),
    // `Mark deposit as received` LEFT this menu (ART-92) — it is a named button in the ribbon now. See the
    // footer for why, and note the seat gate did not move with it: `deposit.available` is still what decides
    // whether it exists at all, and it is already narrow (a money seat, an agreed deposit, not yet received).
  ];

  // `♦ Nashville, TN · Friday, October 9, 2026 · T-47 days` — the specimen's header meta line, the part that
  // carries the header's identity. Same construction as the editor's, so the two dialogs read as one family.
  // ART-240 — the run chip. A show in a run shows "Part of {derived run name}" in the header meta line; the
  // name comes from the runs list (a run has no name column, so it is derived from its date range).
  const run = owner?.runId ? (runs.data?.items ?? []).find((r) => r.runId === owner.runId) : null;
  const runLabel = run ? run.name ?? "a run" : null;
  // "· N shows" — the run's show count (§05). Omitted when zero (a run with no shows yet names nothing to count).
  const runShowCount = run?.showCount ?? 0;
  const meta = d
    ? [
        [d.city, d.state].filter(Boolean).join(", "),
        longDate(d.date ?? ""),
        tMinus(d.date ?? "", todayIso()),
        runLabel
          ? `Part of ${runLabel}${runShowCount > 0 ? ` · ${runShowCount} ${runShowCount === 1 ? "show" : "shows"}` : ""}`
          : "",
      ]
        .filter((part) => part !== "")
        .join(" · ")
    : "";

  // The load/error states are the OWNER fetch's — a guest already holds its matrix, so it never sees them.
  if (!guest && show.isLoading) {
    return (
      <Modal title="Show" onClose={onBack} size="3xl">
        <div className={styles.center}>
          <Spinner />
        </div>
      </Modal>
    );
  }

  if (!guest && (show.isError || !owner)) {
    return (
      <Modal title="Show" onClose={onBack} size="3xl">
        <EmptyState
          title="Couldn't load this show"
          description={
            show.error instanceof ApiError
              ? show.error.message
              : "It may have been removed."
          }
          action={<Button onClick={onBack}>Back to Booking</Button>}
        />
      </Modal>
    );
  }

  if (!d) return null;   // unreachable — an owner returned above, a guest always has `d`

  const lineup = d.lineup ?? [];
  // The owning band is part of its OWN bill — the backend seeds it as a headliner slot as an invariant, so a
  // real show's lineup is never empty. But a show that skipped that seed (a tour-created or fixture-seeded
  // show) can arrive with an empty array, and hiding the whole LINEUP row then reads as "no bill" when there
  // is at least this band. So on an own show, fall back to a synthesized headliner for the acting band rather
  // than dropping the row — the band is always an act on its own show, back-out button or not. A guest reads
  // the host's full bill (always incl. the host's headliner), so it never hits this.
  const lineupToShow: LineupSlot[] =
    !guest && lineup.length === 0
      ? [
          {
            slotId: null,
            position: 0,
            slotRole: "headliner",
            artistName: account.data?.artist ?? "",
            friendBandId: ownerBandId,
            spotifyArtistId: null,
            spotifyImageUrl: null,
          },
        ]
      : lineup;
  const place = [d.city, d.state, d.country].filter(Boolean).join(", ");

  return (
    <>
      <Modal
        size="3xl"
        className={styles.modal}
        title={d.venueName || "Untitled show"}
        // Same reading as the editor's header, for the same reason: the title is the VENUE'S OWN NAME, not a
        // label naming the dialog, so ruling 3's 16px tracked all-caps lockup renders it as chrome. ART-88 was
        // reported against BOTH show surfaces and only the editor was changed (2026-08-28) — which left the two
        // dialogs for the same show disagreeing about how that show's name reads. `headline` also carries the
        // ink subtitle, the ticket's "subtext line should be actual black".
        titleVariant="headline"
        subtitle={meta}
        closeLabel="Close show"
        onClose={onBack}
        footer={
          guest ? (
            // A guest reuses the view modal read-only. "Back out" is no longer a footer action (ART-229): it
            // rides the ROWS now — the whole-band decline on the LINEUP row, the personal pass on the MEMBERS
            // row — so the footer keeps only Close and, for a schedule:write member, Edit. The BAND-decline
            // error still surfaces HERE (BandBackOut feeds `backOutError`): a terminal act's exact server
            // reason is worth an always-visible line rather than a toast. No delete / publish / deposit — the
            // show is the inviter's.
            <>
              {backOutError && (
                <span className={styles.footerError} role="alert">
                  {backOutError}
                </span>
              )}
              <div className={styles.footerActions} data-testid="footer-actions">
                <Button variant="ghost" onClick={onBack}>
                  Close
                </Button>
                {canWrite && (
                  <Button variant="primary" onClick={() => onEdit(showId)}>
                    <Icon icon={Pencil} size={16} /> Edit show
                  </Button>
                )}
              </div>
            </>
          ) : (
          <>
            {deleteShow.isError && (
              <span className={styles.footerError} role="alert">
                {errorMessage(
                  deleteShow.error,
                  "Couldn't delete this show, please refresh and try again.",
                )}
              </span>
            )}
            <div className={styles.footerActions} data-testid="footer-actions">
              {/* The OVERFLOW, and it leads the bar (PO, 2026-08-26). The row is right-aligned, so the LAST
              control sits under the thumb / nearest the eye — that place belongs to the named actions, not
              to a lucky dip. Putting `More` first also keeps the named four in a stable order as the menu
              gains and loses items with the seat.
              What lives in it: the actions that LEAVE this show for somewhere else (the run sheet, the guest
              list, the door) plus the money stamp, none of which a person opens the preview to reach. Eight
              ghost pills of equal weight had made the bar a wall with nothing in it looking like the thing
              you came to do. `MenuButton` renders nothing when its list is empty, so a seat with neither
              guests nor money sees no orphan `More`. */}
              <MenuButton
                items={moreActions}
                menuLabel="More show actions"
                placement="above"
              >
                <Icon icon={Ellipsis} size={16} /> More
              </MenuButton>
              {/* A crew member backs out of THEIR OWN spot from the MEMBERS row now (ART-229), not the footer —
                  see MembersLine. There is no share to decline on an own-band show, so no band back-out here. */}
              {canWrite && (
                <Button variant="ghost" onClick={() => onEdit(showId)}>
                  <Icon icon={Pencil} size={16} /> Edit show
                </Button>
              )}
              {/* A reopened (confirmed + closable) show offers "Close out show" here (Ryan, ART-241): re-close
                  without leaving for Outstanding Payments. See `canCloseOutShow`. The per-show "Add to
                  Bandsintown" that used to share this slot is gone (ART-128): Bandsintown export is a bulk CSV
                  from the Booking page now. */}
              {canCloseOutShow && <CloseOutShowButton showId={showId} scope={closeScope} />}
              {/* ART-92, the second half of the members-row move: `Invite members` went to the row it acts
                  on, and the slot it left is taken by the deposit stamp, which was buried in `More`.
                  A deposit is MONEY OWED THIS BAND that has just arrived — the one thing in that drawer
                  somebody opens this dialog specifically to do — and it is self-limiting enough to be a named
                  button rather than a permanent pill: `deposit.available` is false unless the caller holds a
                  money seat AND the deal carries a deposit AND nobody has stamped it yet. So it appears on
                  the handful of shows where it is the point, and on the rest the bar is one control shorter
                  than before. (Un-marking stays a CORRECTION in the editor's 09 Financials — never here.) */}
              {deposit.available && (
                <Button
                  variant="ghost"
                  onClick={deposit.mark}
                  loading={deposit.pending}
                >
                  <Icon icon={CircleDollarSign} size={16} /> Mark deposit received
                </Button>
              )}
              {/* Reopen a SETTLED show straight from the preview footer (ART-241/248), not only from the
                  settlement panel below. Same audited, reason-required, money:full action — one implementation
                  (ReopenShowButton) so the two triggers on this one dialog cannot drift. */}
              {canReopen && d.status === "settled" && (
                <ReopenShowButton showId={showId} variant="ghost">
                  <Icon icon={RotateCcw} size={16} /> Reopen show
                </ReopenShowButton>
              )}
              {canWrite && (
                <Button
                  variant="ghost"
                  tone="danger"
                  onClick={() => setConfirmingDelete(true)}
                  loading={deleteShow.isPending}
                >
                  <Icon icon={Trash2} size={16} /> Delete
                </Button>
              )}
            </div>
          </>
          )
        }
      >
        <div className={styles.body} data-testid="show-preview">
          {/* ── Left rail — the show facts, money-FREE except Guarantee, which is a headline deal figure the
            specimen puts here and which self-gates below. */}
          <aside className={styles.left}>
            {guest ? (
              // A guest's flyer is read-only and served from the guest-reachable route (its bytes live in the
              // host tenant). No dropzone/remove — that is the owner's `FlyerTile`.
              d.flyerMediaId && guestFlyer.url ? (
                <div className={styles.flyerWrap}>
                  <img
                    className={styles.flyerArt}
                    src={guestFlyer.url}
                    alt={`Flyer for ${d.venueName || "this show"}`}
                  />
                </div>
              ) : (
                <div className={styles.flyer} aria-label="No flyer uploaded" />
              )
            ) : (
              <FlyerTile
                detail={owner!}
                version={show.data?.version ?? 0}
                canWrite={canWrite}
              />
            )}

            {/* Settlement completion as a coloured dot + label (Ryan, ART-241, replacing the ART-210 raw
                lifecycle status). It is ALWAYS "Open" or "Completed", never "Settled"/"Confirmed"/etc.
                "Completed" — which locks the data — is the show-lifecycle `settled` state, and that is set by
                exactly one thing: closing the show out in Outstanding Payments (SettleShowAsync). Everything
                else reads "Open". The SETTLEMENT chip inside the panel is a separate axis (the DOS venue
                settle-with-venue state). */}
            <div
              className={styles.statusLine}
              data-status={d.status === "settled" ? "completed" : "open"}
              data-testid="show-status"
            >
              <span className={styles.statusDot} aria-hidden="true" />
              {d.status === "settled" ? "Completed" : "Open"}
            </div>

            <Fact label="Venue" value={d.venueName || "—"} />
            {place && <Fact label="Location" value={place} />}
            <Fact label="Doors" value={formatTime12(d.doorsTime) ?? "—"} />
            <Fact
              label="Capacity"
              value={d.capacity != null ? String(d.capacity) : "—"}
            />
            {/* Contract — "On file · View" when one is attached (Ryan's ART-210 reference). Schedule-tier
                (the flyer/contract are show assets), so it is money-free and shows on every seat. */}
            {d.contractMediaId && <ContractFact mediaId={d.contractMediaId} venueName={d.venueName} />}
            {d.promoter && <Fact label="Promoter" value={d.promoter} />}
            {d.ticketUrl && (
              <div className={styles.fact}>
                <span className={styles.factLabel}>Ticket link</span>
                <a
                  className={styles.link}
                  href={d.ticketUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {d.ticketUrl}
                </a>
              </div>
            )}
            {!guest && d.status === "confirmed" && (
              <AnnouncedFact showId={showId} announcedOn={d.announcedOn ?? null} canWrite={canWrite} />
            )}
          </aside>

          {/* ── Right of the rail — the roster, then the settlement workbench (its own two columns), then the
            day-of-show composition. A no-money seat simply ends after the roster. */}
          <main className={styles.main}>
            {/* The roster: two LABELLED CHIP ROWS, `LINEUP` and `MEMBERS` — the reference's `.roster-line`, a
              78px mono label beside a wrapping chip stack. Each row hides when it is empty rather than
              printing "none yet": a show with no lineup simply has no lineup line. */}
            <section className={styles.roster}>
              {lineupToShow.length > 0 && (
                <div className={styles.rosterLine}>
                  <span className={styles.rosterLabel}>Lineup</span>
                  <div className={styles.chipRow}>
                    {/* `ordered` — a bill runs opener → headliner, so the order is meaning, not arrangement. */}
                    <Rows
                      variant="framed"
                      wrap
                      ordered
                      className={styles.chipStack}
                    >
                      {lineupToShow.map((slot) => (
                        <LineupChip
                          key={slot.slotId ?? slot.position}
                          slot={slot}
                          source={actSource(slot)}
                          share={
                            slot.slotId ? shareBySlot.get(slot.slotId) : undefined
                          }
                        />
                      ))}
                    </Rows>
                    {/* Back the WHOLE BAND off the bill — the trailing action of the row it acts on (ART-229),
                        guest show only. It is guaranteed a home: an accepted guest is on the lineup by
                        construction (the share is minted from a friend band ON a slot, ART-165), so this row
                        exists whenever a band decline is possible. BandBackOut self-gates on schedule:write +
                        an accepted share, so an unanswered invite / a read-only member renders nothing. */}
                    {guest && (
                      <div className={styles.rosterActions}>
                        <BandBackOut
                          showId={showId}
                          guest={guest}
                          bandId={bandId}
                          onDone={onBack}
                          onError={setBackOutError}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* The MEMBERS row is per-band: it shows the ACTING band's own crew on this show and invites
                  more of them — an owner sees/invites their crew, a guest sees/invites its OWN (per-band
                  members, ART-165), never the host's. "Invite members" opens the crew invite (it had been
                  mis-wired to the support-act finder). */}
              <MembersLine
                showId={showId}
                bandId={bandId}
                canWrite={canWrite}
                onInvite={() => setInvitingMembers(true)}
                onBackOut={onBack}
              />
            </section>

            {/* Settlement — money-gated by construction: the panel only calls its money endpoints when the
              caller holds money:full/per_show (the full record) or money:per_person (the "Your pay" chip); a
              no-money seat renders nothing here (the A3 rule). This is where settlement HAPPENS — never the
              edit form.
              A GUEST SEES ITS OWN, not the host's (ART-165): show_finances / show_settlements are PER-BAND
              (873), so the settlement service resolves the guest's own copy under its own band context —
              gated by the GUEST's own money capability, fenced by RLS to the guest's own rows. So a money
              guest sees its own net/deal and a per-person guest its own "Your pay", both w.r.t. their band. */}
            <SettlementPanel showId={showId} showStatus={d.status} dense />

            {/* NO "Shared shows" card and NO inline Day-of-Show here (parity pass, 2026-08-23). Neither is a
              section in the spec or the reference: legacy surfaces share state as BADGES on the lineup chips
              (above) and opens DOS from the footer button, which is where per-show actions live. The inline
              DOS section was also a straight duplicate of that footer button's destination. */}
          </main>
        </div>
      </Modal>
      {confirmingDelete && (
        <AcDialog
          title="Delete this date?"
          message="This removes it from the schedule. This cannot be undone."
          confirmLabel="Delete"
          tone="danger"
          loading={deleteShow.isPending}
          onConfirm={() => {
            setConfirmingDelete(false);
            deleteShow.mutate(showId, { onSuccess: onBack });
          }}
          onCancel={() => setConfirmingDelete(false)}
        />
      )}

      {invitingMembers && (
        <InviteMembersModal
          showId={showId}
          onClose={() => setInvitingMembers(false)}
        />
      )}

      {/* The DOS run sheet, as a modal over the preview — the reference's shape. `DayOfShowSection` needs
          only a showId, so it drops in whole. */}
      {dosOpen && (
        <Modal
          size="2xl"
          title="Day of show"
          subtitle={meta}
          closeLabel="Close day of show"
          onClose={() => setDosOpen(false)}
        >
          <DayOfShowSection showId={showId} />
        </Modal>
      )}
    </>
  );
}

/**
 * Whether a confirmed show is ANNOUNCED (public) — the manual flag that replaced the Bandsintown publish as the
 * booking boards' "this date is public" signal. A schedule-writing seat toggles it; everyone else reads it.
 */
function AnnouncedFact({
  showId,
  announcedOn,
  canWrite,
}: {
  showId: string;
  announcedOn: string | null;
  canWrite: boolean;
}) {
  const setAnnounced = useSetShowAnnounced();
  const announced = announcedOn != null;
  return (
    <div className={styles.fact}>
      <span className={styles.factLabel}>Announced</span>
      {canWrite ? (
        <Checkbox
          checked={announced}
          disabled={setAnnounced.isPending}
          onChange={(next) => setAnnounced.mutate({ showId, announced: next })}
          label={announced ? `Announced ${longDate(announcedOn.slice(0, 10))}` : "Not announced yet"}
        />
      ) : (
        <span className={styles.factValue}>
          {announced ? `Announced ${longDate(announcedOn.slice(0, 10))}` : "Not announced yet"}
        </span>
      )}
    </div>
  );
}

/**
 * One act on the bill, carrying its SHARE state as a chip TINT — the same language the members row uses (a
 * wash + glow in the status hue), rather than a text badge: a coloured pill reads at a glance and, unlike the
 * badge, does not fight the chip's centre line. STATUS-ONLY since ART-165: a friend band on the lineup is
 * auto-invited when the lineup is saved, so there is no manual "Share" affordance here.
 *
 * The share states map to the members row's hues:
 *   • **accepted** → GREEN (attached — the other band accepted and is syncing);
 *   • **invited**  → AMBER (sent, not yet answered);
 *   • **declined** → RED   (refused; still shown, so it does not read as "never invited").
 * An act with no share (our own act, a vendor or free-text act) stays a neutral chip. The word survives as
 * the chip's accessible name, so the state reaches a screen reader that a colour cannot.
 */
function LineupChip({
  slot,
  source,
  share,
}: {
  slot: LineupSlot;
  /** The act's photo source, from the shared `useActPhotoSource` resolver — `ActAvatar` renders it (a band
   *  act via the global avatar route, a vendor act via its artwork), falling back to initials. */
  source: ActPhotoSource;
  share: ShareDto | undefined;
}) {
  const status = share?.status ?? null;
  const name = slot.artistName ?? "";
  return (
    <Row
      variant="framed"
      density="compact"
      // The share state is the chip's TINT (the members-row treatment) — green accepted, amber invited, red
      // declined; unset for an act with no share. See the `.lineupChip` rules for the wash + glow.
      className={styles.lineupChip}
      data-share={status ?? undefined}
      avatar={
        <ActAvatar source={source} initials={orgInitials(name)} size="sm" shape="circle" />
      }
      name={
        // The WHOLE name line centres (`chipLine`), rather than sharing the Row's baseline — the baseline
        // floated the svg AC mark low, because an svg has no text baseline (reported). Now name, mark and role
        // sit on one centre line.
        <span className={styles.chipLine}>
          {/* Name + AC mark are ONE flex item so the mark sits TIGHT to the name (its own 4px margin) rather
              than a role-gap away. The AC mark: this act is a band on Artist CNTRL (`friendBandId`), on both
              sides of a shared show now (ART-165), not just the booking row. */}
          <span className={styles.chipNameGroup}>
            <span className={styles.chipName}>{name}</span>
            {slot.friendBandId && <AcMark title={`${name || "This act"} is on Artist CNTRL`} />}
            {slot.spotifyArtistId && <SpotifyArtistLink spotifyArtistId={slot.spotifyArtistId} name={name} />}
          </span>
          {/* The ROLE rides inside the chip, quieter than the name — it qualifies the act rather than
              labelling a column, which is what a separate role cell made it read as. */}
          {slot.slotRole && (
            <span className={styles.chipRole}>
              {slotRoleLabel(slot.slotRole)}
            </span>
          )}
          {/* The share state is carried by the chip's colour now, not a word — but colour reaches only sighted
              users, so the state survives as the chip's accessible name (the members-row rule). */}
          {status && <span className={styles.srOnly}>{SHARE_WORD[status] ?? status}</span>}
        </span>
      }
    />
  );
}

/** The RSVP states, as the words the chip no longer prints but still announces. */
const RSVP_WORD: Record<string, string> = {
  in: "accepted",
  pending: "pending",
  out: "declined",
};

/** The lineup share states, as the words the tinted chip no longer prints but still announces. */
const SHARE_WORD: Record<string, string> = {
  accepted: "accepted",
  invited: "invited",
  declined: "declined",
};

/**
 * The `MEMBERS` chip row — the band's own crew on this show, and the row that INVITES them.
 *
 * Its own component because members are a SEPARATE fetch: `ShowDetail` carries the lineup but not the crew,
 * and `MemberDto` is a thin join table of ids, so the display name comes from the band's team list (the same
 * source the editor's picker offers).
 *
 * `Invite members` moved here from the footer ribbon (ART-92, *"move invite members to the members row, make
 * it at the far right"*) — the action now sits beside the thing it changes rather than in a bar of eight
 * unrelated pills, and it is what freed the footer slot the deposit stamp came out of the More drawer to
 * fill. That move also changes when this row may hide: it used to return null with no members, which would
 * now take the only invite affordance with it on exactly the show that needs one. It hides only for a seat
 * that cannot write AND has nobody to show.
 */
function MembersLine({
  showId,
  bandId,
  canWrite,
  onInvite,
  onBackOut,
}: {
  showId: string;
  bandId: string;
  canWrite: boolean;
  onInvite: () => void;
  /** Close the preview after the caller backs out of their own spot (MemberBackOut). */
  onBackOut: () => void;
}) {
  const members = useShowMembers(showId);
  const team = useBandTeam();
  const seats = team.data?.items ?? [];
  const rows = members.data?.items ?? [];
  if (rows.length === 0 && !canWrite) return null;

  return (
    <div className={styles.rosterLine}>
      <span className={styles.rosterLabel}>Members</span>
      <div className={styles.chipRow}>
      <Rows variant="framed" wrap className={styles.chipStack}>
        {rows.map((m) => {
          const seat = seats.find(
            (x) => x.personId && x.personId === m.personId,
          );
          // Never surface a raw uuid: a person on the show who is no longer a crew seat still reads as a
          // person. (The editor's chip had this bug and shipped `Member 3f2a1b9c` until 2026-08-23.)
          const name = seat?.displayName ?? "Invited member";
          // The chip's SHORT form (ART-92), but only for an actual name. `"Invited member"` is the
          // placeholder for a person who no longer holds a crew seat — it is two words, so the shortener
          // would render it `Invited M`, which reads like somebody's name and is nobody's.
          const chipName = seat?.displayName ? shortPersonName(seat.displayName) : name;
          const rsvp = m.rsvp ?? "pending";
          return (
            <Row
              key={m.showMemberId}
              variant="framed"
              density="compact"
              // The WHOLE CHIP carries the answer as a wash plus a glow in the status hue — green in, amber
              // awaiting, red passed (PO 2026-08-29 for the wash, ART-92 for the glow and for dropping the
              // word). A tinted pill reads at a glance across a wrapped row of eight in a way a 2px ring on
              // a 28px circle does not, and it leaves the avatar to be a face.
              className={styles.memberChip}
              data-rsvp={rsvp}
              avatar={
                <PersonAvatar
                  personId={m.personId}
                  avatarMediaId={seat?.avatarMediaId}
                  initials={orgInitials(name)}
                  size="sm"
                  shape="circle"
                />
              }
              name={
                <>
                  {/* `Jane D` — ART-92. The full name is still what a screen reader gets, below. */}
                  <span className={styles.chipName}>{chipName}</span>
                  {/* The state was `· pending` / `· out` in the chip's own text until ART-92 asked for the
                      colour to carry it alone (*"Ditch pending or accepted text"*). Visually it now does —
                      but colour reaching only sighted users is how an RSVP becomes invisible to the rest, so
                      the word survives as the chip's accessible name, along with the UNSHORTENED name that
                      `shortPersonName` just abbreviated. Nothing is lost from the row; it is moved off the
                      pixels the PO wanted back. */}
                  <span className={styles.srOnly}>
                    {name} · {RSVP_WORD[rsvp] ?? rsvp}
                  </span>
                </>
              }
            />
          );
        })}
      </Rows>
        {/* The row's trailing actions (ART-229 folds "back out of your own spot" in beside Invite): each
            self-gates, so an owner sees only Invite, a plain crew member only Back out, and an AV member on
            the crew sees both — Invite first, then the destructive Back out at the trailing edge. */}
        <div className={styles.rosterActions}>
          {canWrite && (
            <Button
              variant="ghost"
              size="xs"
              className={styles.memberInvite}
              onClick={onInvite}
            >
              <Icon icon={UserPlus} size={16} /> Invite members
            </Button>
          )}
          <MemberBackOut showId={showId} bandId={bandId} onDone={onBackOut} />
        </div>
      </div>
    </div>
  );
}

/**
 * The flyer tile at the head of the left rail.
 *
 * It was a bare `<div>` reading `DROP FLYER` for as long as it has existed — the caption invited a drop the
 * element could not accept, and it never rendered an attached flyer either, so a show WITH artwork still
 * read as empty. Both halves are fixed here: the tile paints the real image once one is attached, and an
 * empty tile is a genuine `Dropzone` for a seat that may write.
 *
 * A read-only seat gets the poster ground with no caption at all rather than a verb it cannot perform — the
 * legacy tile was display-only (`.sd-flyer` is a background-image well in the reference), so a viewer losing
 * the caption loses nothing that was ever true.
 *
 * The write is the show's own whole-record PUT, exactly as the editor's tile does it, so the two entry
 * points cannot drift into two different writes.
 */
function FlyerTile({
  detail,
  version,
  canWrite,
}: {
  detail: ShowDetail;
  version: number;
  canWrite: boolean;
}) {
  const showId = detail.showId!;
  const art = useMediaObjectUrl(detail.flyerMediaId, null, { variant: "sm" });
  const { uploadMedia } = useUploadMedia();
  const updateShow = useUpdateShow();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onFile(file: File) {
    setBusy(true);
    setErr(null);
    try {
      const asset = await uploadMedia(file, {
        kind: mediaKindForFile(file),
        purpose: "show-flyer",
      });
      await updateShow.mutateAsync({
        showId,
        version,
        body: { ...showBodyFrom(detail), flyerMediaId: asset.mediaId ?? null },
      });
    } catch (e) {
      // The upload never reports success without persistence, so a throw here means nothing was stored.
      setErr(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      setBusy(false);
    }
  }

  // Attached: the artwork itself, at its natural ratio. NOT the 1:1 well the empty tile uses — a gig poster
  // is portrait, and squaring it centre-crops the headline off the top and the date off the bottom.
  if (detail.flyerMediaId && art.url) {
    return (
      <div className={styles.flyerWrap}>
        <img
          className={styles.flyerArt}
          src={art.url}
          alt={`Flyer for ${detail.venueName || "this show"}`}
        />
        {canWrite && (
          <Button
            variant="ghost"
            size="sm"
            className={styles.flyerRemove}
            loading={busy}
            onClick={() => {
              setErr(null);
              updateShow.mutate({
                showId,
                version,
                body: { ...showBodyFrom(detail), flyerMediaId: null },
              });
            }}
          >
            Remove flyer
          </Button>
        )}
      </div>
    );
  }

  // Attached but the bytes have not resolved yet (or failed): hold the well rather than flashing a dropzone
  // over a flyer that exists — offering "drop one" for an image that is merely loading invites a needless
  // re-upload, and on `error` the designed fallback is the empty poster ground, never a broken image.
  if (detail.flyerMediaId && art.loading) {
    return <div className={styles.flyer} aria-label="Loading flyer" />;
  }

  if (!canWrite)
    return <div className={styles.flyer} aria-label="No flyer uploaded" />;

  return (
    <div className={styles.flyerWrap}>
      <Dropzone
        className={styles.flyerDrop}
        // The tile paints the POSTER ground, so the zone's own copy has to be the on-dark palette — the
        // default's ink prompt measured 1.2:1 against it and could not be read at all.
        tone="inverse"
        accept="image/*"
        disabled={busy}
        label="Flyer"
        prompt={busy ? "Uploading…" : "Drop the show artwork"}
        hint="Or click to browse"
        rejectMessage="That file type is not accepted here (image/*)."
        onFile={(f) => void onFile(f)}
      />
      {err && (
        <span className={styles.flyerErr} role="alert">
          {err}
        </span>
      )}
    </div>
  );
}

/**
 * The overflow's deposit action — stamp the agreed deposit as arrived, dated today.
 *
 * A HOOK rather than a component because the action now lives in a menu, and a menu item is DATA (a label,
 * an icon, a handler) rather than markup the child can render for itself. Everything else about it is
 * unchanged: it needs a money seat to read the settlement at all, and it offers nothing unless there is an
 * agreed `depositCents` still carrying no `depositReceivedOn`. A show with no deposit in its deal, or one
 * already received, contributes no item — the menu stays a list of things you can actually do rather than
 * disabled decoration. (Un-marking is a CORRECTION, not an action: it belongs where the amount is edited,
 * the editor's 09 Financials checkbox.)
 *
 * The write is the settlement's own whole-document PUT — `markDepositReceivedBody` builds it, shared with
 * the Booking row's Unpaid chip, which offers the same action from the list.
 */
function useMarkDepositReceived(showId: string): {
  available: boolean;
  pending: boolean;
  mark: () => void;
} {
  // Both read unconditionally before the OR: `||` short-circuits, so folding them into one expression makes
  // the second hook conditional and breaks hook ORDER on the render where the first is true. Same shape as
  // `SettlementPanel`'s canFull/canPerShow/canPerPerson.
  const canFull = useCapability("money:full");
  const canPerShow = useCapability("money:per_show");
  const canMoney = canFull || canPerShow;
  const settlement = useSettlement(showId, canMoney);
  const update = useUpdateSettlement();
  const d = settlement.data?.data;

  const available =
    canMoney &&
    d != null &&
    d.depositCents != null &&
    d.depositCents !== 0 &&
    !d.depositReceivedOn;

  return {
    available,
    pending: update.isPending,
    mark: () => {
      if (!d) return;
      update.mutate({
        showId,
        version: settlement.data?.version ?? 0,
        body: markDepositReceivedBody(d),
      });
    },
  };
}

/**
 * "Close out show" in the preview footer — re-closes a reopened show without a trip to Outstanding Payments
 * (Ryan, ART-241). It POSTs the scope's close-out (the same route + `useCloseOut` the settle box uses), so the
 * show's worksheet is finalized, the deal re-locked and the lifecycle dot flips back to "Completed".
 *
 * `useCloseOut` invalidates the Outstanding-Payments queries; this ALSO invalidates the show read (its prefix
 * catches the settlement sub-key), because the dot + the settlement panel on THIS surface read the show, and
 * without it they would linger on the stale "Open"/in-progress state — the mirror of the reopen hook's own
 * show-invalidation. No confirm: closing out is reversible (Reopen show), matching the settle box's button.
 */
function CloseOutShowButton({
  showId,
  scope,
}: {
  showId: string;
  scope: { kind: string; id: string };
}) {
  const closeOut = useCloseOut();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return (
    <Button
      variant="ghost"
      loading={closeOut.isPending}
      onClick={() =>
        closeOut.mutate(
          { scopeKind: scope.kind, scopeId: scope.id },
          {
            onSuccess: () =>
              void qc.invalidateQueries({ queryKey: ["band", bandId, "show", showId] }),
          },
        )
      }
    >
      <Icon icon={CircleCheck} size={16} /> Close out show
    </Button>
  );
}

/** A label-over-value fact on the left rail. */
function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.fact}>
      <span className={styles.factLabel}>{label}</span>
      <span className={styles.factValue}>{value}</span>
    </div>
  );
}

/**
 * The CONTRACT fact — "● On file · View" when a signed contract is attached (Ryan's ART-210 reference). The
 * media route is authenticated, so `View` opens the object URL `useMediaObjectUrl` fetches with the bearer
 * rather than a bare `href` the server would 401. While the bytes resolve (or if they fail) it still says
 * "On file" — the row's job is to say a contract EXISTS; opening it is the secondary affordance.
 */
function ContractFact({ mediaId, venueName }: { mediaId: string; venueName?: string | null }) {
  const doc = useMediaObjectUrl(mediaId, null);
  return (
    <div className={styles.fact}>
      <span className={styles.factLabel}>Contract</span>
      <span className={styles.contract}>
        <span className={styles.contractDot} aria-hidden="true" />
        On file
        {doc.url && !doc.error && (
          <a className={styles.link} href={doc.url} target="_blank" rel="noreferrer" aria-label={`View contract for ${venueName || "this show"}`}>
            View
          </a>
        )}
      </span>
    </div>
  );
}
